import { useNavigate } from 'react-router-dom';
import { WFAlertHistoryData } from 'src/api/wf/models/WfAlertHistory';
import { useWfAlertHistoryStore } from 'src/api/wf/stores/WfAlertHistoryStore';
import { wfNavigate } from '../../router/WfScreenRegistration';
import { WfScreensMap } from '../../router/WfScreensMap';
import { WF_Screen } from '../../router/WfScreens';
import { useTranslation } from 'react-i18next';

type WFHistoryViewProps = {
  history?: WFAlertHistoryData | null;
  onClose?: () => void;
};

const toDisplayText = (value?: string | null) => {
  if (value === null || value === undefined) return '';

  const trimmed = value.trim();
  if (trimmed === '' || trimmed === 'null') return '';

  return trimmed;
};

export default function WF_HistoryView({ history, onClose }: WFHistoryViewProps) {
  const { setSelectedHistory, historySearchParams } = useWfAlertHistoryStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-start justify-center bg-gray-100">
      <div className="w-full border border-gray-300 bg-white p-6 shadow-sm">
        <div className="mb-4">
          <div className="mb-2 text-sm font-semibold text-gray-700">
            {t('wf.alertHistory.workplaceInfo')}
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm text-gray-700">
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">{t('wf.alertHistory.buildingName')}</span>
              <span className="text-right">{toDisplayText(historySearchParams?.buildingName)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">{t('wf.alertHistory.organizationCode')}</span>
              <span className="text-right">
                {toDisplayText(historySearchParams?.organizationCode)}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">{t('wf.alertHistory.salesOfficeCode')}</span>
              <span className="text-right">{toDisplayText(historySearchParams?.branchCode)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">{t('wf.common.customerNumber')}</span>
              <span className="text-right">
                {toDisplayText(historySearchParams?.customerNumber)}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-gray-300">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
            {t('wf.alertHistory.alertHistoryInfo')}
          </div>
          <div className="px-4">
            {(history?.alertHistoryRecords?.length ?? 0) === 0 && (
              <div className="py-4 text-sm text-gray-500">{t('wf.common.noResults')}</div>
            )}
            {history?.alertHistoryRecords?.map((it, idx) => (
              <div key={it.alertHistoryId}>
                <div className="flex items-center justify-between gap-4 py-3">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">
                      {t('wf.alertHistoryRecord.buildingCode')}
                    </div>
                    <button
                      className="text-sm font-semibold text-blue-700 underline"
                      onClick={() => {
                        setSelectedHistory(it);
                        wfNavigate(navigate, WfScreensMap[WF_Screen.AlertHistoryDetails].path);
                      }}
                    >
                      {it.buildingCode}
                    </button>
                    {(
                      [
                        { labelKey: 'property1', value: it.propertyName1 },
                        { labelKey: 'property2', value: it.propertyName2 },
                        { labelKey: 'property3', value: it.propertyName3 },
                        { labelKey: 'property4', value: it.propertyName4 },
                      ] as { labelKey: string; value: string }[]
                    ).map(({ labelKey, value }) => (
                      <div key={labelKey} className="mt-1">
                        <div className="text-xs text-gray-500">
                          {t(`wf.alertHistoryRecord.${labelKey}`)}
                        </div>
                        <div className="text-xs text-gray-600">{toDisplayText(value)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div>
                      <div className="text-[11px] text-gray-500">
                        {t('wf.alertHistoryRecord.alertsReceived')}
                      </div>
                      <div className="text-sm font-semibold text-gray-800">{it.alertsReceived}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">
                        {t('wf.alertHistoryRecord.alertsCompleted')}
                      </div>
                      <div className="text-sm font-semibold text-gray-800">
                        {it.alertsCompleted}
                      </div>
                    </div>
                  </div>
                </div>
                {idx !== history.alertHistoryRecords!.length - 1 && (
                  <div className="my-2 border-t border-black/10" />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm border border-gray-400 bg-gray-100 px-4 py-1 text-sm shadow-sm hover:bg-gray-200"
          >
            {t('wf.common.back')}
          </button>
        </div>
      </div>
    </div>
  );
}
